import React from 'react';
import PageHeader from './PageHeader';
import PageTitle from './PageTitle';
import VennITPLogo from "./VennITPLogo";
import ITPModel from "./ITPModel";
import ITPDiagram from "./ITPDiagram";

const Overview = () => {
	return (
		<div className='Overview'>
			<PageHeader title={'Overview'} />

			<div className={'container brief'}>
				<PageTitle title={'An Introduction to The Ideal Team Player'} />
				<p>
					Before diving into the report, it’s worth pointing out that being a team player is absolutely essential for success
					in life. Life is a team sport. Yes, it may sound like a cliché, but if you look around, almost everything we do
					happens in teams. And for anyone serious about succeeding in this team-oriented world, there are three virtues
					or traits that are essential. They are <span className={'font-bold'}>humility</span>, <span className={'font-bold'}>hunger</span>, and <span className={'font-bold'}>smarts</span>. On their own, each of these qualities may
					seem straightforward. But in the context of teamwork, they take on a deeper meaning.
				</p>
				<p>
					How so? What makes this combination so powerful is how the three virtues work together. When someone
					possesses all three, they are far more likely to foster strong teamwork. However, if a person is lacking even one of
					the three, collaboration can become significantly more challenging.
				</p>
			</div>
			<section className="container-description max-w-5xl mx-auto py-8 px-10 overview-descriptions">
				<h2 className="text-[17pt] font-bold mb-4 leading-none">
					A Quick Breakdown of the Three Virtues
				</h2>
				<p className="mb-4">
					This assessment is based on the model from the book <em>The Ideal Team Player</em> by Patrick Lencioni. If you’re
					already familiar with the concepts, feel free to skip ahead to your results on page 2. If not, the following
					overview will help you interpret your report.
				</p>
				<div>
					<section>
						<h3 className="font-bold">HUMBLE</h3>
						<p>
							Ideal team players are humble. They lack excessive ego or concerns about status. Humble people are open to seeking help and learning from others. They are quick to point out and appreciate the contributions of others and slow to seek attention for their own. They share credit, emphasize team over self, and define success collectively rather than individually.
						</p>
					</section>
				</div>
				<div className="grid grid-cols-2 items-center">
					<div>
						<section>
							<h3 className="font-bold">HUNGRY</h3>
							<p>
								Ideal team players are hungry. They are always looking for more. More things to do, more to learn, more responsibility to take on. Hungry people almost never have to be pushed to work harder because they are self-motivated and dedicated. They are constantly thinking about the next step and new opportunities to grow in their work.
							</p>
						</section>

						<section>
							<h3 className="font-bold">SMART</h3>
							<p>
								Ideal team players are smart. They have common sense about people—dealing with others in the most effective way and picking up on the needs and feelings of others. They have good judgment and intuition around the subtleties of group dynamics and are aware of the impact of their own words and actions on others.
							</p>
						</section>
					</div>

					{/* --- right column: centered diagram --- */}
					<div className="flex justify-center">
						<ITPDiagram
							width="300"
							height="300"
							fillHumble="#80929d"
							fillHungry="#263e4b"
							fillSmart="#1c2130"
							strokeColor="#1F2937"
							strokeWidth={'2px'}
							id="overview-diagram"
							titleTextSize={'25px'}
							subtitleTextSize={'19px'}
							smartYPaddingOffset={20}
							hungryYPaddingOffset={35}
							humbleYPaddingOffset={35}
							smartRotPaddingOffset={0}
							hungryRotPaddingOffset={58}
							humbleRotPaddingOffset={58}
						/>
					</div>
				</div>
			</section>
		</div>
	);
};

export default Overview;
